// © Microsoft Corporation. All rights reserved.
import { addAppTraceMarker, addMarkerToPageTimings, getPageTimings } from "./AddMarkerToPageTimings";
import { isProng2AppType, logError } from "./ErrorLogging";
import { memoizeLite, stringifyError } from "@msnews/core-helper";
import { pwPcsResponseCacheDuration, alwaysCachedAppPcsResponseCacheDuration, pwPcsResponseCacheKey, staticServerEndpoint, staticServerEndpointCnDomain } from "@msnews/experiences-constants";
import { get } from "lodash-es";
import { shouldProng2PcsApiUseAkamaiEndpoint } from "@msnews/experimentation-helpers";
import { getLocalStorage } from "@msnews/core";
/**
 * Timeout in miliseconds to terminate PCS or Statics network call.
 */
const timeoutInMs = 15000;
// page timings keys
const pcsRetry = "PcsRetry";
const pcsCached = "PcsCached";
const pcsNoCacheReason = "PcsNoCacheReason";
/**
 * Data Widget Attributes specified by the host page in the Div and override QSPs
 */
let dataWidgetAttributes;
/**
 * This is used to delay writing response to local storage later.
 */
let pcsNetworkResponse;
/**
 * This function calls and gets PCS response for client-setting, user data-info, and core-bundle scripts to render widgets.
 * @param hostPageAttributes contains the specific appType, pageType, cm and pcsAdditionalQsp information.
 * @returns an async response that contains the needed information above.
 */
export async function getPcsResponse(hostPageAttributes) {
    if (!hostPageAttributes) {
        return null;
    }
    const pageTimings = getPageTimings();
    pageTimings[pcsRetry] = 0;
    pageTimings[pcsCached] = 0;
    // Add perf marker and app trace marker on global timings objects
    addMarkerToPageTimings("WidgetsPcsCallStart");
    addAppTraceMarker("WidgetsPcsCall-start");
    // Set dataWidgetAttributes for global function
    dataWidgetAttributes = hostPageAttributes;
    let pcsCachedResponse = null;
    // Get PCS response from cache if specified by the host page by setting "shouldPreFetchPcsResponse" to be true
    const { appType, clientPerf } = dataWidgetAttributes;
    if ((clientPerf && clientPerf.shouldPreFetchPcsResponse)
        //  Temporary remove appType === AppType.WindowsNewsWidgets for CRS spike experiment
        || appType && (appType === "distribution" /* AppType.Distribution */)) {
        // Set handler on global object so this can be called at a later point in time
        setHandlerToWritePcsResponseToCache();
        // Get PCS response from cache.
        pcsCachedResponse = await getPcsResponseFromCache();
    }
    else if (window.__useCachedApp) {
        // Get PCS response from cache.
        pcsCachedResponse = await getPcsResponseFromCache();
    }
    if (!pcsCachedResponse && clientPerf && clientPerf.customPrefetchStrategy) {
        let strategy = null;
        if (typeof clientPerf.customPrefetchStrategy === "string") {
            strategy = get(window, clientPerf.customPrefetchStrategy, null);
        }
        if (strategy) {
            try {
                pcsCachedResponse = await strategy;
                pcsCachedResponse = tryUpdatePcsResponse(pcsCachedResponse, dataWidgetAttributes.useCnDomain);
            }
            catch (error) {
                pcsCachedResponse = null;
            }
        }
    }
    // If "shouldPreFetchPcsResponse" was not specified by the host page or cached response was not valid,
    // proceed making live call to PCS
    if (!pcsCachedResponse) {
        pcsNetworkResponse = await tryGetPcsResponseFromNetwork();
    }
    else {
        pageTimings[pcsCached] = 1;
    }
    // If PCS response from network was not valid, try to return the cached response regardless of the cache duration to avoid blank page.
    if (!pcsNetworkResponse && window.__useCachedApp) {
        pcsCachedResponse = await getPcsResponseFromCache(true);
    }
    // set handler to update the cache for next time; Immediately update PCS response in cache to get new AID/RID
    if (window.__useCachedApp) {
        setTryGetPcsResponseFromNetworkForFutureCaching();
    }
    // Add perf marker and app trace marker on global timings objects
    addMarkerToPageTimings("WidgetsPcsCallEnd");
    addAppTraceMarker("WidgetsPcsCall-end");
    return pcsNetworkResponse || pcsCachedResponse;
}
/**
 * This function calls and gets PCS response from PCS API and gets client-setting, user data-info, and core-bundle scripts to render widgets.
 * This is global promise since we want the ability to call from widget initializer as well as from Peregrine bundles (later in page cycle).
 * @returns an async response that contains the needed information above.
 */
let tryGetPcsResponseFromNetwork = memoizeLite(async () => {
    if (!dataWidgetAttributes) {
        logError("dataWidgetAttributes is null in getPcsResponseFromNetwork", 43715, {});
        return null;
    }
    // Set a 15-second timeout for PCS API call
    let timer;
    let responseData;
    const fetchUrl = buildPcsFetchUrl();
    try {
        const timeoutPromise = new Promise((resolve) => {
            timer = setTimeout(() => {
                // Log an app error if PCS API call timed out
                logError("PCS Widgets API call timed out. Trying to get response from statics", 43713, { url: fetchUrl });
                resolve(null);
            }, timeoutInMs);
        });
        const pcsFetchPromise = fetch(fetchUrl, { credentials: "include" }).then(async (pcsResponse) => {
            if (timer) {
                clearTimeout(timer);
            }
            if (pcsResponse.ok) {
                return await pcsResponse.json();
            }
            const afdAId = tryGetAfdActivityIdFromHeaders(pcsResponse);
            logError("Verify PCS response for pcsData and bundleData for request url", 43716, { fetchUrl, afdAId });
            return null;
        });
        // Race between PCS and a timeout based promise
        responseData = await Promise.race([pcsFetchPromise, timeoutPromise]);
    }
    catch (e) {
        if (timer) {
            clearTimeout(timer);
        }
        // This line of code sends app error only if prong2 page is visible when app type is prong2 app type.
        // If page is not visible in prong2 app type, don't send app error as that is a background refresh.
        const { appType, isCallFromWidgetsChanged } = dataWidgetAttributes || {};
        if (!(isProng2AppType(appType) && isCallFromWidgetsChanged)) {
            // If this is not prong2 app type, send this app error anyways.
            logError("Fetch failed for PCS url", 43715, { originalUrl: fetchUrl, error: stringifyError(e, false) });
        }
    }
    // If response was null or respone was invalid, try get response from static fallback
    if (!validatePcsResponse(responseData)) {
        responseData = await getStaticFallbackPage(fetchUrl);
    }
    // RETRY! If response was still not valid, try getting from static fallback again as a last chance.
    // Make sure that user network is online before retrying.
    const online = (navigator && navigator.onLine) || true;
    if (online && !validatePcsResponse(responseData)) {
        responseData = await getStaticFallbackPage(fetchUrl, true);
    }
    return tryUpdatePcsResponse(responseData, dataWidgetAttributes.useCnDomain);
});
/**
 * Get static fall back page if PCS call is not successful
 * @param url - Widget Url
 * @param thisIsRetry - This is used to send different set of app errors
 * @returns StaticResponse
 */
export async function getStaticFallbackPage(url, thisIsRetry = false) {
    const { instance, staticsEnv } = dataWidgetAttributes && dataWidgetAttributes.staticsAttributes || {};
    const pageTimings = getPageTimings();
    pageTimings[pcsRetry] = (pageTimings[pcsRetry] || 0) + 1;
    if (instance) {
        url += `&instance=${instance}`;
    }
    if (staticsEnv) {
        url += `&staticsEnv=${staticsEnv}`;
    }
    const originalUrl = new URL(url);
    let staticUrl = url.replace(originalUrl.hostname, staticServerEndpoint);
    if (originalUrl.hostname.includes(".cn")) {
        staticUrl = url.replace(originalUrl.hostname, staticServerEndpointCnDomain);
    }
    // Set a 15-second timeout for Static call
    let timer;
    try {
        const timeoutPromise = new Promise((resolve) => {
            timer = setTimeout(() => {
                // Log an app error if Static Response timed out
                const [attempt, errorId] = thisIsRetry ? ["second", 52009] : ["first", 52006];
                logError(`PCS static Response timed out in ${attempt} attempt`, errorId, { originalUrl: url, staticUrl: staticUrl });
                resolve(null);
            }, timeoutInMs);
        });
        const staticFetchPromise = fetch(staticUrl, { credentials: "include" }).then(async (staticResponse) => {
            if (timer) {
                clearTimeout(timer);
            }
            const afdAId = tryGetAfdActivityIdFromHeaders(staticResponse);
            if (staticResponse.ok) {
                return await staticResponse.json();
            }
            else {
                // log error for not loading static page
                const [attempt, errorId] = thisIsRetry ? ["second", 52010] : ["first", 52004];
                logError(`Response not-ok in ${attempt} attempt to get PCS staticview`, errorId, { originalUrl: url, staticUrl: staticUrl, afdAId });
                return null;
            }
        });
        // Race between PCS and a timeout based promise
        const responeFromRace = await Promise.race([staticFetchPromise, timeoutPromise]);
        return responeFromRace;
    }
    catch (e) {
        if (timer) {
            clearTimeout(timer);
        }
        // log exception while loading static page
        const [attempt, errorId] = thisIsRetry ? ["second", 52011] : ["first", 52005];
        logError(`Exception in ${attempt} attempt to get PCS static response`, errorId, { error: stringifyError(e, false), originalUrl: url, staticUrl: staticUrl });
        return null;
    }
}
/**
 * Get PCS response from storage if it was stored in last 24 hours and if it is valid.
 * @param ignoreDuration - Ignore cache duration and return cached response
 * @returns PCS response from browser storage
 */
export async function getPcsResponseFromCache(ignoreDuration = false) {
    try {
        const key = getPcsResponseCacheKey();
        let pcsResponse = JSON.parse(localStorage.getItem(key));
        if (!pcsResponse && window.__bridgeSdk && window.__useCachedApp) {
            // try to get PCS from native storage
            const cachedResponseStr = await window.__bridgeSdk.promiseWithTimeout?.(window.__bridgeSdk.loadDataAsync?.({ key }), 500).then((res) => res ? JSON.parse(res).value : null);
            if (cachedResponseStr) {
                pcsResponse = JSON.parse(cachedResponseStr);
            }
        }
        const { appType } = dataWidgetAttributes || {};
        const storageTimestamp = pcsResponse && pcsResponse.storageTimestamp;
        const pcsUrlCached = pcsResponse && pcsResponse.pcsUrl;
        const currentPcsUrl = buildPcsFetchUrl();
        if (!pcsResponse) {
            // NOTE: JSON.parse(null|0|false) doesn't throw exception, so we need to check for falsy values explicitly
            logPcsNoCacheReason("Empty cache response");
        }
        if (
        // for always cached app in offline mode, return valid cached response immediately without checking cache duration
        ((window.__useCachedApp && !navigator.onLine)
            // if PCS from network failed, return cached response immediately without checking cache duration
            || ignoreDuration)
            && pcsUrlCached === currentPcsUrl && validatePcsResponse(pcsResponse)) {
            return pcsResponse;
        }
        if (appType && appType === "winWidgets" /* AppType.WindowsNewsWidgets */) {
            if (dataWidgetAttributes.useCnDomain && shouldProng2PcsApiUseAkamaiEndpoint(pcsResponse)) {
                // if the PCS cache contains the PCS API Akamai migration flight, set pcs env to Akamai endpoint for the next PCS request
                dataWidgetAttributes.pcsEnvironment = "assets";
            }
            // Temporary remove the Prong2's cache for CRS spike experiment
            // Return after the akamai endpoint check
            return null;
        }
        const cacheDuration = window.__useCachedApp ? alwaysCachedAppPcsResponseCacheDuration : pwPcsResponseCacheDuration;
        const now = new Date().getTime();
        const isCacheStale = storageTimestamp ? (now - storageTimestamp) >= cacheDuration : true;
        if (!isCacheStale
            // Check if pcs cached url matches current pcs url
            && pcsUrlCached === currentPcsUrl
            // Validate cached response
            && validatePcsResponse(pcsResponse)) {
            return pcsResponse;
        }
        else {
            if (isCacheStale) {
                logPcsNoCacheReason("Cache is stale");
            }
            else if (pcsUrlCached !== currentPcsUrl) {
                logPcsNoCacheReason("Cached PCS URL does not match current PCS URL");
            }
            else {
                logPcsNoCacheReason("Cached PCS response is not valid");
            }
        }
    }
    catch (e) {
        const msg = "Error reading PCS response from local storage";
        logPcsNoCacheReason(msg);
        logError(msg, 52007, { error: stringifyError(e, true) });
    }
    return null;
}
/**
 * log reason for not caching PCS response in LoadTime Telemetry for debugging
 * @param reason Reason for not caching PCS response
 */
function logPcsNoCacheReason(reason) {
    const pageTimings = getPageTimings();
    if (!pageTimings[pcsNoCacheReason]) {
        pageTimings[pcsNoCacheReason] = reason;
    }
}
/**
 * This handler will enable us to call PCS API later point in time and write response to local storage.
 */
function setHandlerToWritePcsResponseToCache() {
    window._pw_cachePcsResponse = async () => {
        try {
            const pcsResponse = await tryGetPcsResponseFromNetwork();
            updateCache(pcsResponse);
        }
        catch (e) {
            logError("Error writing PCS response to local storage", 52008, { error: stringifyError(e, true) });
        }
    };
}
/**
 * For always cached app, we want to make sure that the PCS flight info and bundle info is matching with the GitConfig. So we will use a App cache manager to manage the cache flow.
 */
function setTryGetPcsResponseFromNetworkForFutureCaching() {
    // immediately update the cache for next time asynchronously, then resolve the
    const getResponseFromNetworkPromise = new Promise((resolve) => {
        tryGetPcsResponseFromNetwork().then((response) => {
            updateCache(response);
            resolve(response);
        });
    });
    window._pw_tryGetPcsResponseFromNetwork = getResponseFromNetworkPromise;
}
/**
 * a helper function to update cache
 * @param pcsResponse response object to be stored in cache
 */
function updateCache(pcsResponse) {
    if (pcsResponse) {
        pcsResponse.storageTimestamp = new Date().getTime();
        pcsResponse.pcsUrl = buildPcsFetchUrl();
        const key = getPcsResponseCacheKey();
        localStorage.setItem(key, JSON.stringify(pcsResponse));
        if (window.__bridgeSdk && window.__useCachedApp) {
            // update cache in native storage
            window.__bridgeSdk.saveDataAsync?.({ key, value: JSON.stringify(pcsResponse) }).catch((e) => {
                logError("Error writing PCS response to native storage", 52008, { error: stringifyError(e, true) });
            });
        }
    }
    else {
        logError("PCS response was null", 52008, {});
    }
}
/**
 * Clear PCS response from browser storage
 */
export function clearPcsCache() {
    // for always cached app, never clear the cache, we only update the cache when we get the new PCS response.
    // this is to make sure the app works in offline mode.
    if (window.__useCachedApp) {
        return;
    }
    localStorage.removeItem(getPcsResponseCacheKey());
}
/**
 * Check if PCS response is valid
 * @param PCS response
 * @returns true if response is valid, false otherwise
 */
function validatePcsResponse(response) {
    return response
        && response.pcsData
        && response.pcsData.info
        && response.pcsData.clientSettings
        && response.bundleData
        && response.bundleData.bootstrappingRefs;
}
/**
 * Update response per business logic
 */
function tryUpdatePcsResponse(response, useCnDomain) {
    if (!validatePcsResponse(response)) {
        return null;
    }
    // PCS currently does not send this property, so we need to use the host page provided value to set it
    // Add useCnDomain in client settings so all subsequent endpoints will be switched to use .cn instead of .com
    response.pcsData.clientSettings.useCnDomain = useCnDomain;
    // Add widget attribute on client settings so bundles can read required properties
    response.pcsData.clientSettings.widgetAttributes = dataWidgetAttributes;
    return response;
}
/**
 * Get PCS response cache key from Constant, AppType and PageType
 * @returns Cache Key
 */
function getPcsResponseCacheKey() {
    if (!dataWidgetAttributes) {
        return "";
    }
    const { appType, cm, pageType } = dataWidgetAttributes;
    return `${pwPcsResponseCacheKey}_${appType}_${pageType}_${cm}`;
}
/**
 * Build PCS fetch url from DataWidgetAttributes
 * @returns PCS fetch url
 */
function buildPcsFetchUrl() {
    if (!dataWidgetAttributes) {
        return "";
    }
    const { appType, useCnDomain, pageType, pcsEnvironment = "www", pcsAdditionalQsp } = dataWidgetAttributes;
    const isMarketSelectorEnabled = getLocalStorage().getItem("Prong2MarketSelectorEnabled");
    const pcsUrl = getLocalStorage().getItem("Prong2PcsUrl");
    if (isMarketSelectorEnabled === "1" && pcsUrl && pcsUrl.includes("/pcs/api/widget/")) {
        // PCS url in 1S response contains locale and region query params, so we need to remove them
        return `${pcsUrl}${pcsAdditionalQsp ? `&${pcsAdditionalQsp.split("&").filter(param => !param.startsWith("locale=") && !param.startsWith("region=")).join("&")}` : ""}`;
    }
    return `https://${pcsEnvironment}.msn.${useCnDomain ? "cn" : "com"}/pcs/api/widget/${appType}/${pageType || ""}${pcsAdditionalQsp ? `?${pcsAdditionalQsp}` : ""}`;
}
/**
 * Return AFD provided activity id from response header.
 * @param response from server
 * @returns AFD activity id
 */
function tryGetAfdActivityIdFromHeaders(response) {
    if (!response || !response.headers) {
        return "";
    }
    // Return string sequence representing the values of the retrieved header or null if this header is not set.
    // This doesn't throw error if not found.
    return response.headers.get("x-msedge-ref") || "";
}
/**
 * TO BE USED FOR UNIT TESTING ONLY
 */
export function setLocalVariables(attributes) {
    if (attributes) {
        dataWidgetAttributes = attributes;
    }
    tryGetPcsResponseFromNetwork.cache.clear();
}
/**
 * Clear memoize cache.
 */
export function clearNetworkCachedResponse() {
    if (tryGetPcsResponseFromNetwork) {
        tryGetPcsResponseFromNetwork.cache.clear();
    }
}
//# sourceMappingURL=GetPcsResponse.js.map